/**
 * Data layer for `no-code-rule-builder-with-natural-language-prompts`. Thin typed fetch wrappers over /api/v1/no-code-rule-builder-with-natural-language-prompts.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface NoCodeRuleBuilderWithNaturalLanguagePromptsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListNoCodeRuleBuilderWithNaturalLanguagePromptsResponse {
	count: number;
	items: NoCodeRuleBuilderWithNaturalLanguagePromptsRow[];
}

export interface CreateNoCodeRuleBuilderWithNaturalLanguagePromptsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/no-code-rule-builder-with-natural-language-prompts";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListNoCodeRuleBuilderWithNaturalLanguagePromptsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListNoCodeRuleBuilderWithNaturalLanguagePromptsResponse>(res);
}

export async function create(body: CreateNoCodeRuleBuilderWithNaturalLanguagePromptsBody): Promise<NoCodeRuleBuilderWithNaturalLanguagePromptsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<NoCodeRuleBuilderWithNaturalLanguagePromptsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const noCodeRuleBuilderWithNaturalLanguagePromptsQueryKeys = {
	all: ["no-code-rule-builder-with-natural-language-prompts"] as const,
	list: () => [...noCodeRuleBuilderWithNaturalLanguagePromptsQueryKeys.all] as const,
	detail: (id: string) => [...noCodeRuleBuilderWithNaturalLanguagePromptsQueryKeys.all, id] as const,
};
